import React, { ReactNode } from "react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "../ui/dialog";

interface MessageBoxProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  content?: ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
  width?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  className?: string;
  closeAfter?: boolean;
}

const MessageBox: React.FC<MessageBoxProps> = ({
  isOpen,
  title,
  message,
  content,
  onConfirm,
  onCancel,
  onClose,
  width = "max-w-md",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  className = "",
  closeAfter = true,
}) => {
  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    if (closeAfter) onClose();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (closeAfter) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`fixed top-1/2 left-1/2 w-[90%] ${width} transform -translate-x-1/2 -translate-y-1/2 rounded-lg ${className}`}>
        <DialogTitle className={`text-lg font-semibold`}>{title}</DialogTitle>
        {content || (
          <>
            <DialogDescription className="mt-2 text-gray-600">{message}</DialogDescription>
            <DialogFooter className="mt-4 flex justify-end gap-3">
              <Button variant={"secondary"} onClick={handleCancel}>
                {cancelLabel}
              </Button>
              {onConfirm && <Button onClick={handleConfirm}>{confirmLabel}</Button>}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MessageBoxSkeleton: React.FC<MessageBoxProps & { children?: ReactNode }> = ({ isOpen, onClose, children, title = "Loading...", width = "1600px" }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`fixed top-1/2 left-1/2 w-[90%] transform -translate-x-1/2 -translate-y-1/2 rounded-lg max-w-[${width}]`}>
        <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default MessageBox;

export { MessageBox, MessageBoxSkeleton };
